import styled from "@emotion/styled";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { FONT_WEIGHT_SEMI_BOLD, IconButton } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { getNodeSlotIdSelector } from "@skilitics/nautilus-core-designer-data";

import { useFeatureSwitch } from "../../../application";
import { useContentHostId } from "../../../backend";
// Deep import: the screenshots barrel would create an import cycle via useScreenshotCapture -> l10n.
import { useScreenshotUrl } from "../../../media/screenshots/useScreenshotUrl.ts";
import { useExportNodeDesign } from "../../../slots/useExportNodeDesign";
import { useNodeSnapshotHandler } from "../../../snapshots/useSnapshotHandler.ts";
import { getDesignerNodeByIdSelector, getManifestSelector } from "../../../state/contextualSelectors/index.ts";
import { useAddNodeTemplate, useNodeTemplateCategories } from "../api/index.ts";
import { NodeTemplateForm, templateThumbnailSize, type FormValues } from "../shared";
import { useGetThumbnailAssetId } from "../shared/useGetThumbnailAssetId.ts";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  width: 480px;

  background-color: ${({ theme }) => theme.surface};
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
  flex: 0 0 65px;

  padding: 0 20px;

  color: ${({ theme }) => theme.generalTextIcon};
  background-color: ${({ theme }) => theme.surfaceSoft};

  font-size: ${({ theme }) => theme.fontSizeLarge};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
`;

interface Props {
  nodeId: number;
  thumbnailSourceNodeId?: number;
  onSaved: () => void;
  onCancel: () => void;
}

export const SaveTemplate = ({ nodeId, thumbnailSourceNodeId, onSaved, onCancel }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const isSuperUser = useFeatureSwitch("super-user");
  const exportNodeDesign = useExportNodeDesign();
  const getSlotIdById = useSelector(getNodeSlotIdSelector);
  const getManifest = useSelector(getManifestSelector);
  const getById = useSelector(getDesignerNodeByIdSelector);
  const { addAsync, status: addStatus } = useAddNodeTemplate();
  const hostId = useContentHostId();
  const { categories, status: fetchCategoriesStatus } = useNodeTemplateCategories();

  const nodeSnapshotHandler = useNodeSnapshotHandler(nodeId);

  const storedScreenshotUrl = useScreenshotUrl({ nodeId: thumbnailSourceNodeId ?? nodeId });

  const getThumbnailAssetId = useGetThumbnailAssetId();

  const initialValues: FormValues = {
    title: "",
    description: "",
    tags: [],
    scope: "user",
    category: "information",
  };

  // Prefer a live snapshot of the node on screen; when the node isn't rendered
  // (e.g. saving from the scene view) fall back to the stored canvas screenshot.
  const createDefaultThumbnail = useMemo(() => {
    if (nodeSnapshotHandler) {
      return async (): Promise<Blob> =>
        await nodeSnapshotHandler({
          type: "image/webp",
          width: templateThumbnailSize.width,
          height: templateThumbnailSize.height,
          scaleFactor: 2,
        });
    }
    if (storedScreenshotUrl) {
      return async (): Promise<Blob> => await (await fetch(storedScreenshotUrl)).blob();
    }
    return undefined;
  }, [nodeSnapshotHandler, storedScreenshotUrl]);

  const handleSubmit = async (values: FormValues) => {
    const node = getById(nodeId);
    const { roles: allRoles = [] } = getManifest(node.typeId).terminus;
    if (allRoles.length === 0) {
      throw new Error(`Node type ${node.typeId} does not have any roles defined.`);
    }
    const slotId = getSlotIdById(node.id);
    if (!slotId) return;
    const content = exportNodeDesign(node.id, { slotId, action: "save" });

    // Uploads the thumbnail if it's local (currently, a default).
    const thumbnailAssetId = await getThumbnailAssetId(values.thumbnail);

    await addAsync({
      typeId: node.typeId,
      roles: node.role ? [node.role] : allRoles, // considering single role only. change column to varchar to lock in
      title: values.title,
      description: values.description || undefined,
      tags: values.tags,
      content,
      scope: values.scope === "module" ? { type: "module", moduleId: hostId } : { type: values.scope },
      category: values.category,
      thumbnailAssetId,
    });
    onSaved();
  };

  return (
    <Container data-test="save-template-container">
      <Heading data-test="heading-container">
        <span>{formatMessage("title")}</span>
        <IconButton icon="xmark" onClick={onCancel} data-test="close-button" />
      </Heading>
      <Content data-test="content-container">
        {fetchCategoriesStatus === "pending" && <div>Loading...</div>}
        {fetchCategoriesStatus === "error" && <div>Error fetching categories...</div>}
        {fetchCategoriesStatus === "success" && (
          <NodeTemplateForm
            categories={categories}
            initialValues={initialValues}
            createDefaultThumbnail={createDefaultThumbnail}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            canSetSystemScope={isSuperUser}
            canAddCategories={isSuperUser}
            saving={addStatus === "pending"}
          />
        )}
      </Content>
    </Container>
  );
};

const formatStrings = {
  title: {
    en: "Save Template",
  },
} satisfies FormatMessages;
